import type { Directive, DirectiveBinding } from 'vue';
import { usePermissionStore } from '@/stores/permissions';
import type { PermissionCategory } from '@/types/permissions';

/**
 * Permission Directives - Clean & Reactive
 *
 * Usage:
 * v-can-write="'CALCULATOR'"
 * v-can-read="'ANALYSIS'"
 * v-can-delete="'TARGETS'"
 *
 * Features:
 * - Reactive updates when permissions change
 * - Type-safe module names
 * - Clean template syntax
 * - Automatic show/hide behavior
 */

interface PermissionElement extends HTMLElement {
  _permissionCleanup?: () => void;
}

// Helper function to create permission directive
const createPermissionDirective = (
  action: 'read' | 'write' | 'delete'
): Directive<PermissionElement, PermissionCategory> => {
  return {
    mounted(el: PermissionElement, binding: DirectiveBinding<PermissionCategory>) {
      const moduleKey = binding.value;
      if (!moduleKey) {
        console.warn(`[v-can-${action}] Module key is required`);
        return;
      }

      const permissionStore = usePermissionStore();

      // Store original display style
      const originalDisplay = el.style.display || '';

      // Function to check permission and update visibility
      const updateVisibility = () => {
        let hasPermission = false;

        try {
          switch (action) {
            case 'read':
              hasPermission = permissionStore.canRead(moduleKey);
              break;
            case 'write':
              hasPermission = permissionStore.canWrite(moduleKey);
              break;
            case 'delete':
              hasPermission = permissionStore.canDelete(moduleKey);
              break;
          }
        } catch (error) {
          console.error(`[v-can-${action}] Error checking permission:`, error);
          hasPermission = false;
        }

        // Update element visibility
        el.style.display = hasPermission ? originalDisplay : 'none';
      };

      // Initial check
      updateVisibility();

      // Watch for permission changes (reactive)
      // Note: Store's reactive properties will trigger updates automatically
      // through Vue's reactivity system when used in components

      // Store cleanup function for unmount
      el._permissionCleanup = () => {
        // Restore original display
        el.style.display = originalDisplay;
      };
    },

    updated(el: PermissionElement, binding: DirectiveBinding<PermissionCategory>) {
      // Re-check permissions on update
      const moduleKey = binding.value;
      if (!moduleKey) return;

      const permissionStore = usePermissionStore();
      const originalDisplay = el.style.display || '';

      let hasPermission = false;
      try {
        switch (action) {
          case 'read':
            hasPermission = permissionStore.canRead(moduleKey);
            break;
          case 'write':
            hasPermission = permissionStore.canWrite(moduleKey);
            break;
          case 'delete':
            hasPermission = permissionStore.canDelete(moduleKey);
            break;
        }
      } catch (error) {
        console.error(`[v-can-${action}] Error checking permission on update:`, error);
        hasPermission = false;
      }

      el.style.display = hasPermission ? originalDisplay : 'none';
    },

    unmounted(el: PermissionElement) {
      // Cleanup
      if (el._permissionCleanup) {
        el._permissionCleanup();
        delete el._permissionCleanup;
      }
    }
  };
};

// Export individual directives
export const vCanRead = createPermissionDirective('read');
export const vCanWrite = createPermissionDirective('write');
export const vCanDelete = createPermissionDirective('delete');

// Export as plugin for easier registration
export default {
  install(app: any) {
    app.directive('can-read', vCanRead);
    app.directive('can-write', vCanWrite);
    app.directive('can-delete', vCanDelete);

    console.log('📋 Permission directives registered: v-can-read, v-can-write, v-can-delete');
  }
};